import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { invoicingService } from '../../services/invoicingService';
import type { UnbilledTransaction, ConsolidatedInvoiceResult } from '../../types/invoicing';

interface ConsolidatedInvoicingModalProps {
  businessProfileId: string;
  isOwner: boolean;
  onClose: () => void;
}

type ViewMode = 'list' | 'review' | 'result';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// Previous month, with year rollback in January — matches the ticket's
// default period (last month's transactions are what you'd bill now).
function getPreviousMonth(): { year: number; month: number } {
  const now = new Date();
  const month = now.getMonth(); // 0-11, so "now.getMonth()" is already last month's 1-based number
  return month === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month };
}

// Mirrors the backend's grouping key (KSeFInvoiceService.IssueConsolidatedInvoicesAsync)
// so the preview accurately predicts how many invoices will actually be created.
function groupKey(t: UnbilledTransaction): string {
  if (t.buyerUserId) return `user:${t.buyerUserId}`;
  if (t.buyerEmail) return `email:${t.buyerEmail.trim().toLowerCase()}`;
  return `payment:${t.paymentId}`;
}

export function ConsolidatedInvoicingModal({ businessProfileId, isOwner, onClose }: ConsolidatedInvoicingModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const previousMonth = getPreviousMonth();
  const [selectedYear, setSelectedYear] = useState(previousMonth.year);
  const [selectedMonth, setSelectedMonth] = useState(previousMonth.month);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const [transactions, setTransactions] = useState<UnbilledTransaction[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<ViewMode>('list');
  const [isIssuing, setIsIssuing] = useState(false);
  const [results, setResults] = useState<ConsolidatedInvoiceResult[] | null>(null);

  const now = new Date();
  const availableYears = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear()];
  const monthNames = [
    t('months.january', 'Styczeń'), t('months.february', 'Luty'), t('months.march', 'Marzec'),
    t('months.april', 'Kwiecień'), t('months.may', 'Maj'), t('months.june', 'Czerwiec'),
    t('months.july', 'Lipiec'), t('months.august', 'Sierpień'), t('months.september', 'Wrzesień'),
    t('months.october', 'Październik'), t('months.november', 'Listopad'), t('months.december', 'Grudzień'),
  ];

  useEffect(() => {
    fetchTransactions();
    setSelectedIds(new Set());
    setMode('list');
    setResults(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, businessProfileId, token]);

  const fetchTransactions = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await invoicingService.getUnbilledTransactions(businessProfileId, selectedYear, selectedMonth, token);
      setTransactions(data.transactions);
    } catch {
      setError(t('invoicing.loadError', 'Nie udało się pobrać transakcji'));
    } finally {
      setIsLoading(false);
    }
  };

  const unbilled = useMemo(() => (transactions || []).filter(x => !x.isInvoiced), [transactions]);
  const selectedTransactions = useMemo(
    () => unbilled.filter(x => selectedIds.has(x.paymentId)),
    [unbilled, selectedIds]
  );
  const groups = useMemo(() => {
    const map = new Map<string, UnbilledTransaction[]>();
    for (const tx of selectedTransactions) {
      const key = groupKey(tx);
      const list = map.get(key) || [];
      list.push(tx);
      map.set(key, list);
    }
    return Array.from(map.values());
  }, [selectedTransactions]);

  const allUnbilledSelected = unbilled.length > 0 && unbilled.every(x => selectedIds.has(x.paymentId));

  const toggleAll = () => {
    setSelectedIds(allUnbilledSelected ? new Set() : new Set(unbilled.map(x => x.paymentId)));
  };

  const toggleOne = (paymentId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  };

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} zł`;
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleIssue = async () => {
    if (!token || groups.length === 0) return;
    setIsIssuing(true);
    try {
      const response = await invoicingService.issueConsolidatedInvoices(
        businessProfileId,
        selectedTransactions.map(x => x.paymentId),
        token
      );
      setResults(response.createdInvoices);
      setMode('result');
      const failed = response.createdInvoices.filter(r => r.status === 'Error').length;
      if (failed === 0) {
        showToast('success', t('invoicing.issueSuccess', 'Faktury zostały wystawione'));
      } else {
        showToast('error', t('invoicing.issuePartialError', `${failed} z ${response.createdInvoices.length} faktur nie udało się wysłać`));
      }
      fetchTransactions();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to issue invoices';
      showToast('error', t('common.error'), message);
      setMode('list');
    } finally {
      setIsIssuing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong">
          <h2 className="text-lg font-semibold text-white">
            {t('invoicing.title', 'Fakturowanie zbiorcze')}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {mode === 'list' && (
          <>
            {/* Period Selectors */}
            <div className="p-4 border-b border-border-strong flex gap-3">
              <div className="relative flex-1">
                <button
                  onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 bg-surface-card dark:bg-white/10 border border-border-strong rounded-lg hover:bg-surface-card-hover dark:hover:bg-white/15 transition-colors"
                >
                  <div className="text-left">
                    <span className="text-xs text-ink-muted">{t('reports.year', 'Rok')}</span>
                    <p className="text-sm text-ink-primary font-medium">{selectedYear}</p>
                  </div>
                  <svg className="w-4 h-4 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showYearPicker && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-surface-card border border-border-strong rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                    {availableYears.map(year => (
                      <button
                        key={year}
                        onClick={() => { setSelectedYear(year); setShowYearPicker(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors ${year === selectedYear ? 'bg-brand-primary text-white' : 'text-ink-primary hover:bg-surface-card-hover'}`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative flex-1">
                <button
                  onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 bg-surface-card dark:bg-white/10 border border-border-strong rounded-lg hover:bg-surface-card-hover dark:hover:bg-white/15 transition-colors"
                >
                  <div className="text-left">
                    <span className="text-xs text-ink-muted">{t('reports.month', 'Miesiąc')}</span>
                    <p className="text-sm text-ink-primary font-medium">{monthNames[selectedMonth - 1]}</p>
                  </div>
                  <svg className="w-4 h-4 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showMonthPicker && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-surface-card border border-border-strong rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                    {MONTHS.map(m => (
                      <button
                        key={m}
                        onClick={() => { setSelectedMonth(m); setShowMonthPicker(false); }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors ${m === selectedMonth ? 'bg-brand-primary text-white' : 'text-ink-primary hover:bg-surface-card-hover'}`}
                      >
                        {monthNames[m - 1]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <svg className="w-8 h-8 animate-spin text-brand-primary" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <p className="text-sm text-ink-secondary mt-3">{t('invoicing.loading', 'Ładowanie transakcji...')}</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-sm text-brand-warm-deep mb-4">{error}</p>
                  <button onClick={fetchTransactions} className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm">
                    {t('common.retry', 'Spróbuj ponownie')}
                  </button>
                </div>
              ) : !transactions || transactions.length === 0 ? (
                <p className="text-sm text-ink-muted text-center py-8">
                  {t('invoicing.noTransactions', 'Brak transakcji w wybranym okresie')}
                </p>
              ) : (
                <div className="bg-surface-card dark:bg-white/10 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-ink-secondary text-left border-b border-border-strong">
                        <th className="p-3 w-10">
                          {unbilled.length > 0 && (
                            <input
                              type="checkbox"
                              checked={allUnbilledSelected}
                              onChange={toggleAll}
                              className="w-4 h-4 rounded border-border-strong text-brand-primary focus:ring-brand-primary"
                            />
                          )}
                        </th>
                        <th className="p-3">{t('invoicing.buyer', 'Klient')}</th>
                        <th className="p-3">{t('invoicing.description', 'Opis')}</th>
                        <th className="p-3">{t('invoicing.date', 'Data')}</th>
                        <th className="p-3 text-right">{t('invoicing.amount', 'Kwota')}</th>
                        <th className="p-3">{t('invoicing.status', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, index) => (
                        <tr
                          key={tx.paymentId}
                          className={`${selectedIds.has(tx.paymentId) ? 'bg-brand-primary/10' : index % 2 === 1 ? 'bg-surface-app-elevated' : ''}`}
                        >
                          <td className="p-3">
                            {!tx.isInvoiced && (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(tx.paymentId)}
                                onChange={() => toggleOne(tx.paymentId)}
                                className="w-4 h-4 rounded border-border-strong text-brand-primary focus:ring-brand-primary"
                              />
                            )}
                          </td>
                          <td className="p-3">
                            <p className="text-ink-primary">{tx.buyerName}</p>
                            {tx.buyerEmail && <p className="text-xs text-ink-muted">{tx.buyerEmail}</p>}
                          </td>
                          <td className="p-3 text-ink-primary">{tx.description}</td>
                          <td className="p-3 text-ink-secondary">{formatDate(tx.date)}</td>
                          <td className="p-3 text-right font-medium text-brand-primary">{formatCurrency(tx.amount)}</td>
                          <td className="p-3">
                            {tx.isInvoiced ? (
                              <span className="text-xs px-2 py-1 rounded bg-brand-confirm/30 text-brand-confirm-deep" title={tx.kSeFReferenceNumber || undefined}>
                                {t('invoicing.invoicedStatus', 'Zafakturowano')}{tx.invoiceNumber ? ` (${tx.invoiceNumber})` : ''}
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded bg-brand-accent/20 text-brand-accent">
                                {t('invoicing.unbilledStatus', 'Do faktury')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border-strong flex items-center justify-between">
              <p className="text-sm text-white/80">
                {selectedTransactions.length === 0
                  ? t('invoicing.nothingSelected', 'Nie zaznaczono żadnych transakcji')
                  : t('invoicing.selectionSummary', `${selectedTransactions.length} transakcji → ${groups.length} ${groups.length === 1 ? 'faktura' : 'faktury'}`)}
              </p>
              {!isOwner ? (
                <p className="text-xs text-white/60">
                  {t('invoicing.ownerOnly', 'Tylko właściciel firmy może wystawiać faktury zbiorcze')}
                </p>
              ) : (
                <button
                  onClick={() => setMode('review')}
                  disabled={selectedTransactions.length === 0}
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('invoicing.reviewAndIssue', 'Przejrzyj i wystaw')}
                </button>
              )}
            </div>
          </>
        )}

        {mode === 'review' && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-sm text-white/80">
                {t('invoicing.reviewIntro', 'Poniższe faktury zostaną wystawione i wysłane do KSeF:')}
              </p>
              {groups.map((group, i) => {
                const total = group.reduce((sum, x) => sum + x.amount, 0);
                return (
                  <div key={i} className="bg-surface-card dark:bg-white/10 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-ink-primary">{group[0].buyerName}</span>
                      <span className="font-bold text-brand-primary">{formatCurrency(total)}</span>
                    </div>
                    <ul className="text-xs text-ink-secondary space-y-1">
                      {group.map(tx => (
                        <li key={tx.paymentId}>{tx.description} — {formatCurrency(tx.amount)}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-border-strong flex items-center justify-end gap-3">
              <button
                onClick={() => setMode('list')}
                disabled={isIssuing}
                className="px-4 py-2 text-white/80 hover:text-white transition-colors text-sm"
              >
                {t('common.cancel', 'Anuluj')}
              </button>
              <button
                onClick={handleIssue}
                disabled={isIssuing}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium disabled:opacity-60"
              >
                {isIssuing ? t('invoicing.issuing', 'Wystawianie...') : t('invoicing.confirmIssue', 'Wystaw faktury do KSeF')}
              </button>
            </div>
          </>
        )}

        {mode === 'result' && results && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {results.map((r, i) => (
                <div key={i} className="bg-surface-card dark:bg-white/10 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-ink-primary font-medium">{r.buyerName}</p>
                    <p className="text-xs text-ink-secondary">{r.invoiceNumber || '-'}</p>
                    {r.errorMessage && <p className="text-xs text-brand-warm-deep mt-1">{r.errorMessage}</p>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${r.status === 'Error' ? 'bg-brand-warm/30 text-brand-warm-deep' : 'bg-brand-confirm/30 text-brand-confirm-deep'}`}>
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border-strong flex justify-end">
              <button
                onClick={() => setMode('list')}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors text-sm font-medium"
              >
                {t('common.done', 'Gotowe')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
